import React, { useState, useMemo, useEffect } from "react";
import { 
  Box, 
  IconButton, 
  Tooltip, 
  ToggleButton, 
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Divider
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Download } from "@mui/icons-material";
import * as XLSX from 'xlsx-js-style';
import { useAtom } from "jotai";
import reportsAtoms from "../../../atoms/reports/ReportsAtom";
import settingsAtom from '../../../atoms/settings/SettingsAtom';
import { CustomPagination } from '../../../shared/Helper';

const Table = ({ trackerData, partsData, currentProject }) => {
  const [activeTable, setActiveTable] = useState("trackers");
  const [selectedBlocks, setSelectedBlocks] = useState([]);
  const [blockOptions, setBlockOptions] = useState([]);
  const [blockOrder, setBlockOrder] = useState([]);
  const [reportsData] = useAtom(reportsAtoms);
  const [, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const { projectList } = useAtom(settingsAtom)[0];
  const projectInfo = projectList.find(o => o.id === parseInt(localStorage.getItem("project_id")));

  const transformedTrackerData = useMemo(() => {
    if (!trackerData) return [];
    
    const trackerTypes = new Set();
    Object.values(trackerData).forEach(blockData => {
      blockData.forEach(item => {
        if (item.tracker_type) {
          trackerTypes.add(item.tracker_type);
        }
      });
    });

    return Array.from(trackerTypes).map((trackerType, index) => {
      const row = {
        id: index + 1,
        trackerType: trackerType
      };

      Object.entries(trackerData).forEach(([blockId, blockData]) => {
        const blockItem = blockData.find(item => item.tracker_type === trackerType);
        row[`block${blockId}`] = blockItem ? blockItem.count : 0;
      });

      return row;
    });
  }, [trackerData]);

  const transformedPartsData = useMemo(() => {
    if (!partsData || !reportsData.partDetails || !Array.isArray(reportsData.partDetails)) return [];
    
    const bomIds = new Set();
    Object.keys(partsData).forEach(key => {
      const parts = key.split('_');
      if (parts.length === 2) {
        bomIds.add(parts[1]);
      }
    });


    return Array.from(bomIds).map((bomId, index) => {
      const partDetail = reportsData.partDetails.find(part => {
        return part?.bom_id && part.bom_id.toString() === bomId;
      });
      
      if (!partDetail) {
        console.warn(`Part details not found for bomId: ${bomId}`);
        const row = {
          id: index + 1,
          partNumber: `Part-${bomId}`,
          partDesc: 'Description not available'
        };

        Object.entries(reportsData.blockNames || {}).forEach(([blockId, blockName]) => {
          const bomKey = `${blockId}_${bomId}`;
          row[`block${blockId}`] = partsData[bomKey] || 0;
        });

        return row;
      }

      const row = {
        id: index + 1,
        partNumber: partDetail.part_name || 'N/A',
        partDesc: partDetail.description || 'N/A'
      };

      Object.entries(reportsData.blockNames || {}).forEach(([blockId, blockName]) => {
        const bomKey = `${blockId}_${bomId}`;
        row[`block${blockId}`] = partsData[bomKey] || 0;
      });

      return row;
    }).filter(Boolean);
  }, [partsData, reportsData.partDetails, reportsData.blockNames]);

  useEffect(() => {
    if (reportsData.blockNames) {
      const blocks = Object.entries(reportsData.blockNames).map(([blockId, blockName]) => ({
        value: `block${blockId}`,
        label: blockName
      }));
      
      setBlockOrder(blocks.map(block => block.value));
      setBlockOptions(blocks);
      setSelectedBlocks(blocks.map(block => block.value));
    }
  }, [reportsData.blockNames]);

  const handleTableChange = (event, newValue) => {
    if (newValue !== null) {
      setActiveTable(newValue);
    }
  };

  const handleBlockChange = (event) => {
    const value = event.target.value;
    
    if (value.includes("unselect-all")) {
      setSelectedBlocks([]);
      return;
    }
    
    const sortedBlocks = blockOrder.filter(block => value.includes(block));
    setSelectedBlocks(sortedBlocks);
  };

  const currentData = activeTable === "trackers" ? transformedTrackerData : transformedPartsData;

  const columns = useMemo(() => {
    const baseColumns = [];
    const minBlockColWidth = 80;
    const maxBlockColWidth = 150;
    const totalTableWidth = 1200;
    const numBlockCols = selectedBlocks.length || 1;
    let blockColWidth = Math.floor((totalTableWidth - 280 - 400) / numBlockCols);
    blockColWidth = Math.max(minBlockColWidth, Math.min(maxBlockColWidth, blockColWidth));

    if (activeTable === "trackers") {
      baseColumns.push({
        field: "trackerType",
        headerName: "Tracker Type",
        width: 400,
        flex: 1,
        minWidth: 200,
        headerClassName: "custom-header",
        sortable: false,
        filterable: false,
        renderHeader: () => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              fontWeight: 600,
              marginBottom: "4px",
            }}
          >
            <span>Tracker Type</span>
          </Box>
        ),
        renderCell: (params) => (
          <Box
            sx={{
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "1.2",
              py: 1,
              whiteSpace: 'normal',
              wordBreak: 'break-word'
            }}
          >
            {params.value}
          </Box>
        ),
      });
    } else {
      baseColumns.push(
        {
          field: "partNumber",
          headerName: "Part Number",
          width: 140,
          minWidth: 100,
          maxWidth: 180,
          headerClassName: "custom-header",
          sortable: false,
          filterable: false,
          renderHeader: () => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                fontWeight: 600,
              }}
            >
              <span>Part Number</span>
            </Box>
          ),
          renderCell: (params) => (
            <Box
              sx={{
                fontWeight: 600,
                fontSize: "14px",
                color: "#1976d2",
              }}
            >
              {params.value}
            </Box>
          ),
        },
        {
          field: "partDesc",
          headerName: "Part Desc",
          width: 400,
          flex: 2,
          minWidth: 250,
          maxWidth: 600,
          headerClassName: "custom-header",
          sortable: false,
          filterable: false,
          renderHeader: () => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                fontWeight: 600,
              }}
            >
              <span>Part Desc</span>
            </Box>
          ),
          renderCell: (params) => (
            <Tooltip title={params.value} placement="top">
              <Box
                sx={{
                  fontSize: "14px",
                  lineHeight: "1.4",
                  py: 1,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word'
                }}
              >
                {params.value}
              </Box>
            </Tooltip>
          ),
        }
      );
    }

    blockOrder.forEach((blockKey) => {
      if (selectedBlocks.includes(blockKey)) {
        const blockOption = blockOptions.find(option => option.value === blockKey);
        if (blockOption) {
          baseColumns.push({
            field: blockKey,
            headerName: blockOption.label,
            width: blockColWidth,
            minWidth: minBlockColWidth,
            maxWidth: maxBlockColWidth,
            headerAlign: "center",
            align: "center",
            type: "number",
            sortable: false,
            filterable: false,
          });
        }
      }
    });

    return baseColumns;
  }, [activeTable, selectedBlocks, blockOptions, blockOrder]);

  const handleDownload = () => {
    const worksheetData = [];

    worksheetData.push(['Project Number', projectInfo?.project_number || '']);
    worksheetData.push(['Project Name', projectInfo?.name || '']);
    worksheetData.push(['Report', activeTable === "trackers" ? "Tracker Summary" : "Parts Summary"]);
    worksheetData.push(['Report Date', new Date().toLocaleDateString()]);
    worksheetData.push([]);
    worksheetData.push([]);
    
    const headers = [];
    if (activeTable === "trackers") {
      headers.push("Tracker Type");
    } else {
      headers.push("Part Number");
      headers.push("Part Description");
    }
    
    selectedBlocks.forEach((blockKey) => {
      const blockOption = blockOptions.find(option => option.value === blockKey);
      if (blockOption) {
        headers.push(blockOption.label);
      }
    });
    
    worksheetData.push(headers);
    currentData.forEach(row => {
      const dataRow = [];
      
      if (activeTable === "trackers") {
        dataRow.push(row.trackerType || '');
      } else {
        dataRow.push(row.partNumber || '');
        dataRow.push(row.partDesc || '');
      }
      
      selectedBlocks.forEach((blockKey) => {
        dataRow.push(row[blockKey] || 0);
      });
      
      worksheetData.push(dataRow);
    });
    
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    
    const headerRowIndex = 6;
    for (let r = 0; r < 4; r++) {
      const cellAddress = XLSX.utils.encode_cell({ r, c: 0 });
      if (worksheet[cellAddress]) {
        worksheet[cellAddress].s = {
          font: { bold: true },
          alignment: { horizontal: 'left' }
        };
      }
    }
    
    for (let c = 0; c < headers.length; c++) {
      const cellAddress = XLSX.utils.encode_cell({ r: headerRowIndex, c });
      if (worksheet[cellAddress]) {
        worksheet[cellAddress].s = {
          font: { bold: true },
          alignment: { horizontal: 'center' },
          fill: { fgColor: { rgb: 'E8F4FD' } },
          border: {
            top: { style: 'thin', color: { rgb: '000000' } },
            bottom: { style: 'thin', color: { rgb: '000000' } },
            left: { style: 'thin', color: { rgb: '000000' } },
            right: { style: 'thin', color: { rgb: '000000' } }
          }
        };
      }
    }
    
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    for (let R = headerRowIndex + 1; R <= range.e.r; ++R) {
      for (let C = 0; C < headers.length; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!worksheet[cellAddress]) {
          worksheet[cellAddress] = { v: '', t: 's' };
        }
        worksheet[cellAddress].s = {
          border: {
            top: { style: 'thin', color: { rgb: '000000' } },
            bottom: { style: 'thin', color: { rgb: '000000' } },
            left: { style: 'thin', color: { rgb: '000000' } },
            right: { style: 'thin', color: { rgb: '000000' } }
          },
          alignment: { horizontal: 'center' }
        };
      }
    }

    const colWidths = [];
    if (activeTable === "trackers") {
      colWidths.push({ wch: 40 });
    } else {
      colWidths.push({ wch: 20 });
      colWidths.push({ wch: 60 });
    }

    selectedBlocks.forEach(() => {
      colWidths.push({ wch: 15 });
    });
    
    worksheet['!cols'] = colWidths;
  
    const sheetName = activeTable === "trackers" ? "Tracker Summary" : "Parts Summary";
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    const fileName = `${sheetName} ${projectInfo.name}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    
    setSnackbar({
      open: true,
      message: `${sheetName} downloaded successfully!`,
      severity: "success",
    });

  };

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 2,
        gap: 2
      }}>
        <ToggleButtonGroup
          value={activeTable}
          exclusive
          onChange={handleTableChange}
          aria-label="table selection"
          sx={{
            "& .MuiToggleButton-root": {
              px: 3,
              py: 1,
              fontWeight: 500,
              fontSize: "16px",
              margin: "0 4px",
              border: "2px solid #fbc4bf",
              color: "#9ca3af",
              "&.Mui-selected": {
                backgroundColor: "#f16253",
                color: "white",
                borderColor: "#f16253",
                "&:hover": {
                  backgroundColor: "#e55449",
                },
              },
              "&:hover": {
                backgroundColor: "#fef2f2",
              },
            },
          }}
        >
          <ToggleButton value="trackers" aria-label="trackers">
            Trackers
          </ToggleButton>
          <ToggleButton value="parts" aria-label="parts">
            Parts
          </ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="block-select-label">Select Blocks</InputLabel>
            <Select 
                labelId="block-select-label"
                id="block-select"
                multiple
                value={selectedBlocks}
                onChange={handleBlockChange}
                input={<OutlinedInput label="Select Blocks" />}
                renderValue={(selected) => `${selected.length} blocks selected`}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 400,
                    },
                  },
                }}
              >
                <MenuItem value="unselect-all">
                  <ListItemText primary="Unselect All" />
                </MenuItem>
                <Divider />
                {blockOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox checked={selectedBlocks.indexOf(option.value) > -1} />
                    <ListItemText primary={option.label} />
                  </MenuItem>
                ))}
              </Select>

          </FormControl>

          {/* Download Button */}
          <Tooltip title="Download Excel">
            <IconButton onClick={handleDownload}>
              <Download fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box sx={{ height: "calc(100vh - 280px)", width: "100%", overflow: "auto" }}>
        <DataGrid
          rows={currentData}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10]}
          disableColumnMenu
          disableSelectionOnClick
          slots={{ pagination: CustomPagination }}
        />
      </Box>
    </Box>
  );
};

export default Table;